import * as Phaser from "phaser";
import { v4 as uuidv4 } from "uuid";
import type {
    AreaData,
    AreaDataProperties,
    AreaDataProperty,
    FocusablePropertyData,
    TooltipPropertyData,
    JitsiRoomPropertyData,
    ListenerMegaphonePropertyData,
    LockableAreaPropertyData,
    MatrixRoomPropertyData,
    OpenFilePropertyData,
    OpenWebsitePropertyData,
    PersonalAreaPropertyData,
    PlayAudioPropertyData,
    SpeakerMegaphonePropertyData,
    LivekitRoomPropertyData,
    HighlightPropertyData,
} from "@workadventure/map-editor";
import { PersonalAreaAccessClaimMode } from "@workadventure/map-editor";
import * as Sentry from "@sentry/svelte";
import { getSpeakerMegaphoneAreaInfo, getSpeakerMegaphoneAreaName } from "@workadventure/map-editor/src/Utils";
import { Jitsi } from "@workadventure/shared-utils";
import type { Unsubscriber } from "svelte/store";
import { get } from "svelte/store";
import type { Member } from "@workadventure/messages";
import { FilterType } from "@workadventure/messages";
import { AbortError } from "@workadventure/shared-utils/src/Abort/AbortError";
import { LL } from "../../../../i18n/i18n-svelte";
import { analyticsClient } from "../../../Administration/AnalyticsClient";
import { scriptUtils } from "../../../Api/ScriptUtils";
import { localUserStore } from "../../../Connection/LocalUserStore";
import { Room } from "../../../Connection/Room";
import { ADMIN_URL, JITSI_PRIVATE_MODE, JITSI_URL } from "../../../Enum/EnvironmentVariable";
import {
    audioManagerFileStore,
    audioManagerVisibilityStore,
    audioManagerVolumeStore,
} from "../../../Stores/AudioManagerStore";
import { chatVisibilityStore, chatZoneLiveStore } from "../../../Stores/ChatStore";
/**
 * @DEPRECATED - This is the old way to show trigger message
 import { layoutManagerActionStore } from "../../../Stores/LayoutManagerStore";
 */
import {
    inJitsiStore,
    inLivekitStore,
    inOpenWebsite,
    isListenerStore,
    isSpeakerStore,
    listenerWaitingMediaStore,
    listenerSharingCameraStore,
    requestedCameraState,
    requestedMicrophoneState,
    silentStore,
} from "../../../Stores/MediaStore";
import { externalMeetingEnded, externalMeetingStarted } from "../../../ExternalModule/ExternalMeetingAnalytics";
import { jitsiMeetingEnded, jitsiMeetingStarted } from "../../../WebRtc/JitsiMeetingAnalytics";
import { currentLiveStreamingSpaceStore } from "../../../Stores/MegaphoneStore";
import { notificationPlayingStore } from "../../../Stores/NotificationStore";
import type { CoWebsite } from "../../../WebRtc/CoWebsite/CoWebsite";
import { getImageCoWebsiteTitle, ImageCoWebsite, isImageCoWebsiteUrl } from "../../../WebRtc/CoWebsite/ImageCoWebsite";
import { JitsiCoWebsite } from "../../../WebRtc/CoWebsite/JitsiCoWebsite";
import { SimpleCoWebsite } from "../../../WebRtc/CoWebsite/SimpleCoWebsite";
import { coWebsites } from "../../../Stores/CoWebsiteStore";
import {
    ON_ACTION_TRIGGER_BUTTON,
    ON_ACTION_TRIGGER_ENTER,
    ON_ICON_TRIGGER_BUTTON,
} from "../../../WebRtc/LayoutManager";
import { gameManager } from "../GameManager";
import type { OpenCoWebsite } from "../GameMapPropertiesListener";
import type { GameScene } from "../GameScene";
import { mapEditorAskToClaimPersonalAreaStore } from "../../../Stores/MapEditorStore";
import {
    canRequestVisitCardsStore,
    requestVisitCardsStore,
    selectedChatIDRemotePlayerStore,
} from "../../../Stores/GameStore";

import type { MessageUserJoined } from "../../../Connection/ConnexionModels";
import { navChat } from "../../../Chat/Stores/ChatStore";
import type { Area } from "../../Entity/Area";
import { extensionModuleStore } from "../../../Stores/GameSceneStore";
import type { ChatRoom } from "../../../Chat/Connection/ChatConnection";
import { userIsConnected } from "../../../Stores/MenuStore";
import { popupStore } from "../../../Stores/PopupStore";
import { getMegaphoneSpaceFields } from "../../../Streaming/MegaphoneSpaceFields";
import PopupCowebsite from "../../../Components/PopUp/PopupCowebsite.svelte";
import JitsiPopup from "../../../Components/PopUp/PopUpJitsi.svelte";
import PopUpTab from "../../../Components/PopUp/PopUpTab.svelte";
import { selectedRoomStore } from "../../../Chat/Stores/SelectRoomStore";
import FilePopup from "../../../Components/PopUp/FilePopup.svelte";
import { isInsidePersonalAreaStore } from "../../../Stores/PersonalDeskStore";
import { currentPlayerLockableAreasStore, type LockableAreaEntry } from "../../../Stores/CurrentPlayerAreaLockStore";
import { areaPropertyVariablesManagerStore } from "../../../Stores/AreaPropertyVariablesStore";
import { touchScreenManager } from "../../../Touch/TouchScreenManager";

import Rectangle = Phaser.Geom.Rectangle;
import Color = Phaser.Display.Color;

/**
 * Represents the state of an active megaphone zone (speaker or listener).
 * Used to track nested megaphone zones and handle role transitions.
 */
interface MegaphoneZoneState {
    spaceName: string;
    role: "speaker" | "listener";
    propertyId: string;
    seeAttendees: boolean;
    chatEnabled: boolean;
    allowTalking: boolean;
    waitingLink: string | undefined;
}

type AreaDataPropertyUpdate = {
    [Type in AreaDataProperty["type"]]: {
        type: Type;
        oldProperty: Extract<AreaDataProperty, { type: Type }>;
        newProperty: Extract<AreaDataProperty, { type: Type }>;
    };
}[AreaDataProperty["type"]];

export function getAreaProximitySpaceName(rawName: string, fallbackId: string): string {
    const roomID = rawName.trim().length === 0 ? fallbackId : rawName;
    return Jitsi.slugifyJitsiRoomName(roomID, "", true).trim();
}

export class AreasPropertiesListener {
    private scene: GameScene;

    /**
     * Opened by Areas only, per property
     */
    private openedCoWebsites = new Map<string, OpenCoWebsite>();
    private coWebsitesActionTriggers = new Map<string, string>();
    private _isMicrophoneActiveBeforeLivekitRoom: boolean = false;
    private _isVideoActiveBeforeLivekitRoom: boolean = false;
    private _requestedMicrophoneStateSubscription: Unsubscriber | undefined;
    private _requestedCameraStateSubscription: Unsubscriber | undefined;
    private _areaPropertyVariablesSubscription: Unsubscriber | undefined;
    private _variableChangesSubscription: Unsubscriber | undefined;

    private actionTriggerCallback: Map<string, () => void> = new Map<string, () => void>();

    /**
     * Tracks active megaphone zones the player is currently inside.
     * Key is the property ID of the zone.
     * This enables handling nested speaker/listener zones by switching roles
     * instead of joining/leaving the space.
     */
    private activeMegaphoneZones: Map<string, MegaphoneZoneState> = new Map();

    constructor(scene: GameScene) {
        this.scene = scene;

        // Subscribe to area property variable changes to update lock state store
        // We subscribe to the manager store to handle cases where the manager is set later
        this._areaPropertyVariablesSubscription = areaPropertyVariablesManagerStore.subscribe((manager) => {
            // Clean up previous subscription when manager changes
            if (this._variableChangesSubscription) {
                this._variableChangesSubscription();
                this._variableChangesSubscription = undefined;
            }

            if (!manager) {
                return;
            }

            // Subscribe to the manager's variable changes
            this._variableChangesSubscription = manager.variableChanges.subscribe((change) => {
                if (!change || change.key !== "lock") {
                    return;
                }

                currentPlayerLockableAreasStore.update((list) =>
                    list.map((entry) =>
                        entry.areaId === change.areaId && entry.propertyId === change.propertyId
                            ? { ...entry, lockState: Boolean(change.value) }
                            : entry,
                    ),
                );
            });
        });
    }

    public onEnterAreasHandler(areasData: AreaData[], areas?: Area[]): void {
        for (const areaData of areasData) {
            if (!areaData.properties) {
                continue;
            }

            // Add new notification to show at the user that he entered a new area
            if (areaData.name && areaData.name !== "") {
                notificationPlayingStore.playNotification(areaData.name, "icon-tool-area.png", areaData.id);
            }

            // get area from area data
            const area = areas?.find((area) => area.areaData.id === areaData.id);

            // Check if area has lockableAreaPropertyData and add to list
            const lockableProperty = areaData.properties.find(
                (property): property is LockableAreaPropertyData => property.type === "lockableAreaPropertyData",
            );
            if (lockableProperty) {
                const manager = get(areaPropertyVariablesManagerStore);
                const lockState = manager?.getVariable(areaData.id, lockableProperty.id, "lock") ?? false;
                const entry: LockableAreaEntry = {
                    areaId: areaData.id,
                    propertyId: lockableProperty.id,
                    lockState: Boolean(lockState),
                    areaName: areaData.name ?? "",
                };
                currentPlayerLockableAreasStore.update((list) => {
                    if (list.some((e) => e.areaId === entry.areaId && e.propertyId === entry.propertyId)) {
                        return list;
                    }
                    return [...list, entry];
                });
            }

            for (const property of areaData.properties) {
                this.addPropertyFilter(property, areaData, area);
            }
        }
    }

    public onUpdateAreasHandler(
        area: AreaData,
        oldProperties: AreaDataProperties | undefined,
        newProperties: AreaDataProperties | undefined,
    ): void {
        const propertiesTreated = new Set<string>();

        if (newProperties === undefined) {
            return;
        }

        // Check if area has lockableAreaPropertyData and update store if player is in this area
        const lockableProperty = newProperties.find(
            (property): property is LockableAreaPropertyData => property.type === "lockableAreaPropertyData",
        );
        const oldLockableProperty = oldProperties?.find(
            (property): property is LockableAreaPropertyData => property.type === "lockableAreaPropertyData",
        );

        // Compare allowedTags arrays to detect permission changes
        // Uses Set-based comparison for order-independent comparison
        const allowedTagsChanged =
            lockableProperty &&
            oldLockableProperty &&
            (() => {
                const oldTags = oldLockableProperty.allowedTags ?? [];
                const newTags = lockableProperty.allowedTags ?? [];
                if (oldTags.length !== newTags.length) {
                    return true;
                }
                // Use Set for order-independent comparison
                const oldTagsSet = new Set(oldTags);
                return newTags.some((tag) => !oldTagsSet.has(tag));
            })();

        const areasManager = this.scene.getGameMapFrontWrapper().areasManager;
        const playerInArea = areasManager?.isCurrentPlayerInArea(area.id) ?? false;

        // Handle lockableAreaPropertyData being added or removed
        if (lockableProperty && !oldLockableProperty) {
            if (playerInArea) {
                const manager = get(areaPropertyVariablesManagerStore);
                const lockState = manager?.getVariable(area.id, lockableProperty.id, "lock") ?? false;
                const entry: LockableAreaEntry = {
                    areaId: area.id,
                    propertyId: lockableProperty.id,
                    lockState: Boolean(lockState),
                    areaName: area.name ?? "",
                };
                currentPlayerLockableAreasStore.update((list) => {
                    if (list.some((e) => e.areaId === entry.areaId && e.propertyId === entry.propertyId)) {
                        return list;
                    }
                    return [...list, entry];
                });
            }
        } else if (!lockableProperty && oldLockableProperty) {
            currentPlayerLockableAreasStore.update((list) =>
                list.filter((e) => !(e.areaId === area.id && e.propertyId === oldLockableProperty.id)),
            );
        } else if (allowedTagsChanged && lockableProperty && playerInArea) {
            // Tags changed: trigger store update so UI re-evaluates canLockEntry (button disabled vs enabled)
            currentPlayerLockableAreasStore.update((list) => [...list]);
        }

        // Note: Lock state changes are now handled via area property variables stream,
        // not through WAM property updates

        if (oldProperties !== undefined) {
            for (const oldProperty of oldProperties) {
                const newProperty = newProperties.find((searchedProperty) => searchedProperty.id === oldProperty.id);

                if (JSON.stringify(oldProperty) === JSON.stringify(newProperty)) {
                    propertiesTreated.add(oldProperty.id);
                    continue;
                }

                if (newProperty === undefined) {
                    this.removePropertyFilter(oldProperty, undefined, area);
                } else {
                    this.updatePropertyFilter(oldProperty, newProperty, area);
                }

                propertiesTreated.add(oldProperty.id);
            }
        }

        for (const newProperty of newProperties) {
            if (propertiesTreated.has(newProperty.id)) {
                continue;
            }
            this.addPropertyFilter(newProperty, area);
        }
    }

    public onLeaveAreasHandler(areasData: AreaData[], areas?: Area[]): void {
        for (const areaData of areasData) {
            if (!areaData.properties) {
                continue;
            }
            // Remove notification for area
            notificationPlayingStore.removeNotificationById(areaData.id);

            // get area from area data
            const area = areas?.find((area) => area.areaData.id === areaData.id);

            // Check if leaving area has lockableAreaPropertyData and remove only this area from list
            const lockableProperty = areaData.properties.find(
                (property): property is LockableAreaPropertyData => property.type === "lockableAreaPropertyData",
            );
            if (lockableProperty) {
                currentPlayerLockableAreasStore.update((list) =>
                    list.filter((e) => !(e.areaId === areaData.id && e.propertyId === lockableProperty.id)),
                );

                const areasManager = this.scene.getGameMapFrontWrapper().areasManager;
                const manager = get(areaPropertyVariablesManagerStore);
                const isLocked = manager?.getVariable(areaData.id, lockableProperty.id, "lock");

                if (isLocked === true && areasManager) {
                    // Unlock when area becomes empty is handled by the back on user leave
                    // Update collision to block this player from re-entering the locked area
                    areasManager.updateAreaCollision(areaData.id);
                }
            }

            for (const property of areaData.properties) {
                this.removePropertyFilter(property, area, areaData);
            }

            this.scene.landingAreas = this.scene.landingAreas.filter((landingArea) => landingArea.id !== areaData.id);
        }
    }

    // A map of abortControllers indexed by area property ID that will be triggered when the area if left.
    private abortControllers: Map<string, AbortController> = new Map();

    private addPropertyFilter(property: AreaDataProperty, areaData: AreaData, area?: Area) {
        const abortController = new AbortController();
        this.abortControllers.set(property.id, abortController);
        switch (property.type) {
            case "openWebsite": {
                this.handleOpenWebsitePropertyOnEnter(property, areaData);
                break;
            }
            case "playAudio": {
                this.handlePlayAudioPropertyOnEnter(property);
                break;
            }
            case "focusable": {
                this.handleFocusablePropertiesOnEnter(
                    areaData.x,
                    areaData.y,
                    areaData.width,
                    areaData.height,
                    property,
                );
                break;
            }
            case "highlight": {
                this.handleHighlightPropertyOnEnter(areaData, property);
                break;
            }
            case "jitsiRoomProperty": {
                this.handleJitsiRoomPropertyOnEnter(property);
                break;
            }
            case "livekitRoomProperty": {
                this.handleLivekitRoomPropertyOnEnter(property, abortController.signal).catch((e) => {
                    if (e instanceof AbortError) {
                        return;
                    }
                    console.error(e);
                    Sentry.captureException(e);
                });
                break;
            }
            case "silent": {
                this.handleSilentPropertyOnEnter();
                break;
            }
            case "speakerMegaphone": {
                this.handleSpeakerMegaphonePropertyOnEnter(property, areaData.id, abortController.signal).catch((e) => {
                    console.error(e);
                    Sentry.captureException(e);
                });
                break;
            }
            case "listenerMegaphone": {
                this.handleListenerMegaphonePropertyOnEnter(property, abortController.signal).catch((e) => {
                    console.error(e);
                    Sentry.captureException(e);
                });
                break;
            }
            case "exit": {
                let url = `${property.url}`;
                if (property.areaName && property.areaName !== "") {
                    url = `${property.url}#${property.areaName}`;
                }

                if (this.scene.landingAreas.every((area) => areaData.id !== area.id)) {
                    this.handleExitPropertyOnEnter(url);
                }

                break;
            }
            case "personalAreaPropertyData": {
                this.handlePersonalAreaPropertyOnEnter(property, areaData, area);
                break;
            }
            case "extensionModule": {
                this.handleExtensionModuleAreaPropertyOnEnter(areaData, property.subtype, abortController.signal);
                break;
            }
            case "matrixRoomPropertyData": {
                this.handleMatrixRoomAreaOnEnter(property);
                break;
            }
            case "tooltipPropertyData": {
                this.handleTooltipPropertyOnEnter(property);
                break;
            }
            case "openFile": {
                this.handleOpenFileOnEnter(property, areaData, abortController.signal).catch((error) =>
                    console.error("Error opening File:", error),
                );
                break;
            }

            default: {
                break;
            }
        }
    }

    private updatePropertyFilter(previousProperty: AreaDataProperty, nextProperty: AreaDataProperty, area: AreaData) {
        if (previousProperty.type !== nextProperty.type) {
            throw new Error("Cannot update a property with a different type");
        }
        const { type, oldProperty, newProperty } = {
            type: previousProperty.type,
            oldProperty: previousProperty,
            newProperty: nextProperty,
        } as AreaDataPropertyUpdate;

        const oldAbortController = this.abortControllers.get(oldProperty.id);
        if (oldAbortController) {
            oldAbortController.abort();
            this.abortControllers.delete(oldProperty.id);
        }
        const newAbortController = new AbortController();
        this.abortControllers.set(newProperty.id, newAbortController);

        switch (type) {
            case "openWebsite": {
                this.handleOpenWebsitePropertiesOnLeave(oldProperty);
                this.handleOpenWebsitePropertyOnEnter(newProperty, area);
                break;
            }
            case "playAudio": {
                this.handlePlayAudioPropertyOnUpdate(newProperty);
                break;
            }
            case "focusable": {
                this.handleFocusablePropertiesOnEnter(area.x, area.y, area.width, area.height, newProperty);
                break;
            }
            case "highlight": {
                this.handleHighlightPropertyOnEnter(area, newProperty);
                break;
            }
            case "jitsiRoomProperty": {
                this.handleJitsiRoomPropertyOnLeave(oldProperty);
                this.handleJitsiRoomPropertyOnEnter(newProperty);
                break;
            }
            case "livekitRoomProperty": {
                this.handleLivekitRoomPropertyOnLeave(oldProperty)
                    .then(() => {
                        return this.handleLivekitRoomPropertyOnEnter(newProperty, newAbortController.signal);
                    })
                    .catch((e) => {
                        if (e instanceof AbortError) {
                            return;
                        }
                        console.error(e);
                        Sentry.captureException(e);
                    });
                break;
            }
            case "speakerMegaphone": {
                this.handleSpeakerMegaphonePropertyOnLeave(oldProperty, area.id).catch((e) => {
                    console.error("Error while leaving space");
                    Sentry.captureException(new Error("Error while leaving space"));
                });
                this.handleSpeakerMegaphonePropertyOnEnter(newProperty, area.id, newAbortController.signal).catch(
                    (e) => {
                        console.error(e);
                        Sentry.captureException(e);
                    },
                );
                break;
            }
            case "listenerMegaphone": {
                this.handleListenerMegaphonePropertyOnLeave(oldProperty).catch((e) => {
                    console.error(e);
                    Sentry.captureException(e);
                });
                this.handleListenerMegaphonePropertyOnEnter(newProperty, newAbortController.signal).catch((e) => {
                    console.error(e);
                    Sentry.captureException(e);
                });

                this.recalculateHighlightProperty(area);

                break;
            }
            case "exit": {
                let url = `${newProperty.url}`;
                if (newProperty.areaName && newProperty.areaName !== "") {
                    url = `${newProperty.url}#${newProperty.areaName}`;
                }
                this.handleExitPropertyOnEnter(url);
                break;
            }
            case "personalAreaPropertyData": {
                this.handlePersonalAreaPropertyOnLeave(oldProperty);
                this.handlePersonalAreaPropertyOnEnter(newProperty, area);
                break;
            }
            case "matrixRoomPropertyData": {
                this.handleMatrixRoomAreaOnLeave(oldProperty);
                this.handleMatrixRoomAreaOnEnter(newProperty);
                break;
            }
            case "tooltipPropertyData": {
                this.handleTooltipPropertyOnLeave(oldProperty);
                this.handleTooltipPropertyOnEnter(newProperty);
                break;
            }
            case "openFile": {
                this.handleOpenFileOnLeave(oldProperty);
                this.handleOpenFileOnEnter(newProperty, area, newAbortController.signal).catch((error) =>
                    console.error("Error opening file:", error),
                );
                break;
            }
            case "silent":
            default: {
                break;
            }
        }
    }

    private removePropertyFilter(property: AreaDataProperty, area?: Area, areaData?: AreaData) {
        const abortController = this.abortControllers.get(property.id);
        if (abortController) {
            abortController.abort();
            this.abortControllers.delete(property.id);
        }

        switch (property.type) {
            case "openWebsite": {
                this.handleOpenWebsitePropertiesOnLeave(property);
                break;
            }
            case "playAudio": {
                this.handlePlayAudioPropertyOnLeave();
                break;
            }
            case "focusable": {
                this.handleFocusablePropertiesOnLeave(property);
                break;
            }
            case "highlight": {
                this.handleHighlightPropertiesOnLeave(property);
                break;
            }
            case "jitsiRoomProperty": {
                this.handleJitsiRoomPropertyOnLeave(property);
                break;
            }
            case "livekitRoomProperty": {
                this.handleLivekitRoomPropertyOnLeave(property).catch((e) => {
                    console.error(e);
                    Sentry.captureException(e);
                });
                break;
            }
            case "silent": {
                this.handleSilentPropertyOnLeave();
                break;
            }
            case "speakerMegaphone": {
                this.handleSpeakerMegaphonePropertyOnLeave(property, areaData?.id).catch((e) => {
                    console.error("Error while leaving space");
                    Sentry.captureException(new Error("Error while leaving space"));
                });
                break;
            }
            case "listenerMegaphone": {
                this.handleListenerMegaphonePropertyOnLeave(property).catch((e) => {
                    console.error(e);
                    Sentry.captureException(e);
                });
                break;
            }
            case "personalAreaPropertyData": {
                this.handlePersonalAreaPropertyOnLeave(property, area);
                break;
            }
            case "extensionModule": {
                this.handleExtensionModuleAreaPropertyOnLeave(property.subtype, areaData);
                break;
            }
            case "matrixRoomPropertyData": {
                this.handleMatrixRoomAreaOnLeave(property);
                break;
            }
            case "tooltipPropertyData": {
                this.handleTooltipPropertyOnLeave(property);
                break;
            }
            case "openFile": {
                this.handleOpenFileOnLeave(property);
                break;
            }
            default: {
                break;
            }
        }
    }

    private recalculateHighlightProperty(area: AreaData): void {
        const highlightProperty = area.properties?.find((property) => property.type === "highlight");
        if (highlightProperty) {
            this.handleHighlightPropertyOnEnter(area, highlightProperty);
        }
    }

    private handlePlayAudioPropertyOnEnter(property: PlayAudioPropertyData): void {
        // playAudioLoop is supposedly deprecated. Should we ignore it?
        audioManagerFileStore.playAudio(property.audioLink, this.scene.getMapUrl(), property.volume);
        audioManagerVisibilityStore.set("visible");
    }

    private handleOpenWebsitePropertyOnEnter(property: OpenWebsitePropertyData, areaData?: AreaData): void {
        if (!property.link) {
            return;
        }

        const actionId = "openWebsite-" + uuidv4();

        if (property.newTab) {
            const forceTrigger = localUserStore.getForceCowebsiteTrigger();
            if (forceTrigger || property.trigger === ON_ACTION_TRIGGER_BUTTON) {
                this.coWebsitesActionTriggers.set(property.id, actionId);
                let message = property.triggerMessage;
                if (message === undefined) {
                    message = touchScreenManager.detectPrimaryTouchDevice()
                        ? get(LL).trigger.mobile.newTab()
                        : get(LL).trigger.newTab();
                }

                popupStore.addPopup(
                    PopUpTab,
                    {
                        message: message,
                        click: () => {
                            popupStore.removePopup(actionId);
                            scriptUtils.openTab(property.link as string);
                        },
                        userInputManager: this.scene.userInputManager,
                    },
                    actionId,
                );

                // Create callback and play text message
                // NEW CODE BEFORE REDESIGN. TODO: choose if we keep it
                /*const callback = () => {
                    scriptUtils.openTab(property.link as string), this.scene.CurrentPlayer.destroyText(actionId);
                    this.scene.userInputManager.removeSpaceEventListener(callback);
                    this.actionTriggerCallback.delete(actionId);
                };
                this.scene.CurrentPlayer.playText(actionId, `${message}`, -1, callback);
                this.scene.userInputManager?.addSpaceEventListener(callback);
                this.actionTriggerCallback.set(actionId, callback);*/

                /**
                 * @DEPRECATED - This is the old way to show trigger message
                 layoutManagerActionStore.addAction({
                 uuid: actionId,
                 type: "message",
                 message: message,
                 click: () => {
                 popupStore.removePopup(actionId);
                 scriptUtils.openTab(property.link as string);
                 },
                 userInputManager: this.scene.userInputManager,
                 });
                 */
            } else {
                scriptUtils.openTab(property.link);
            }
            return;
        }

        if (this.openedCoWebsites.has(property.id)) {
            return;
        }

        const coWebsiteOpen: OpenCoWebsite = {
            actionId: actionId,
        };

        this.openedCoWebsites.set(property.id, coWebsiteOpen);

        if (localUserStore.getForceCowebsiteTrigger() || property.trigger === ON_ACTION_TRIGGER_BUTTON) {
            let message = property.triggerMessage;
            if (!message) {
                message = touchScreenManager.detectPrimaryTouchDevice()
                    ? get(LL).trigger.mobile.cowebsite()
                    : get(LL).trigger.cowebsite();
            }

            this.coWebsitesActionTriggers.set(property.id, actionId);

            popupStore.addPopup(
                PopupCowebsite,
                {
                    message: message,
                    click: () => {
                        this.openCoWebsiteFunction(property, coWebsiteOpen, actionId, {
                            targetUrl: this.toCanonicalCowebsiteTargetUrl(property.link ?? ""),
                            triggerProperty: "openWebsite",
                            areaId: areaData?.id,
                            areaName: areaData?.name,
                        });
                    },
                    userInputManager: this.scene.userInputManager,
                },
                actionId,
            );

            // Create callback and play text message
            // TODO: This is the new popups before the new design. Choose if we keep it or not.
            /*const callback = () => {
                this.openCoWebsiteFunction(property, coWebsiteOpen, actionId);
                this.scene.CurrentPlayer.destroyText(actionId);
                this.scene.userInputManager.removeSpaceEventListener(callback);
                this.actionTriggerCallback.delete(actionId);
            };
            this.scene.CurrentPlayer.playText(actionId, `${message}`, -1, callback);
            this.scene.userInputManager?.addSpaceEventListener(callback);
            this.actionTriggerCallback.set(actionId, callback);*/

            /**
             * @DEPRECATED - This is the old way to show trigger message
             layoutManagerActionStore.addAction({
             uuid: actionId,
             type: "message",
             message: message,
             click: () => {
             this.openCoWebsiteFunction(property, coWebsiteOpen, actionId);
             },
             userInputManager: this.scene.userInputManager,
             },
             actionId
             );*/
        } else if (property.trigger === ON_ICON_TRIGGER_BUTTON) {
            let url = property.link ?? "";
            try {
                url = scriptUtils.getWebsiteUrl(property.link ?? "");
            } catch (e) {
                console.error("Error on getWebsiteUrl: ", e);
            }
            const coWebsite = new SimpleCoWebsite(
                new URL(url, this.scene.mapUrlFile),
                property.allowAPI,
                property.policy,
                property.width,
                property.closable,
                property.hideUrl,
            );

            coWebsiteOpen.coWebsite = coWebsite;

            coWebsites.add(coWebsite, undefined, {
                targetUrl: this.toCanonicalCowebsiteTargetUrl(property.link ?? ""),
                triggerProperty: "openWebsite",
                areaId: areaData?.id,
                areaName: areaData?.name,
            });

            //user in zone to open cowesite with only icon
            inOpenWebsite.set(true);
        }
        if (property.trigger == undefined || property.trigger === ON_ACTION_TRIGGER_ENTER) {
            this.openCoWebsiteFunction(property, coWebsiteOpen, actionId, {
                targetUrl: this.toCanonicalCowebsiteTargetUrl(property.link ?? ""),
                triggerProperty: "openWebsite",
                areaId: areaData?.id,
                areaName: areaData?.name,
            });
        }
    }

    private handleFocusablePropertiesOnEnter(
        x: number,
        y: number,
        width: number,
        height: number,
        property: FocusablePropertyData,
    ): void {
        const zoomMargin = property.zoom_margin ? Math.max(0, property.zoom_margin) : undefined;
        this.scene.getCameraManager().enterFocusMode(
            {
                x: x + width * 0.5,
                y: y + height * 0.5,
                width,
                height,
            },
            zoomMargin,
        );
    }

    private mergeZones(zones: AreaData[]) {
        if (!zones || zones.length === 0) return null;

        let left = Number.POSITIVE_INFINITY;
        let top = Number.POSITIVE_INFINITY;
        let right = Number.NEGATIVE_INFINITY;
        let bottom = Number.NEGATIVE_INFINITY;

        for (const z of zones) {
            // assuming origin 0,0 !!
            left = Math.min(left, z.x);
            top = Math.min(top, z.y);
            right = Math.max(right, z.x + z.width);
            bottom = Math.max(bottom, z.y + z.height);
        }

        return new Rectangle(left, top, right - left, bottom - top);
    }

    private handleHighlightPropertyOnEnter(areaData: AreaData, property: HighlightPropertyData): void {
        if (!this.scene.focusFx) {
            // Maybe not supported (Canvas renderer)
            return;
        }
        this.scene.focusFx.attachToArea(areaData);
        this.scene.focusFx.setFeather(property.gradientWidth);
        this.scene.focusFx.setColor(Color.HexStringToColor(property.color));
        this.scene.focusFx.setTargetDarkness(property.opacity);
        this.scene.focusFx.setTransitionDuration(property.duration);
        this.scene.focusFx.show();

        // Rules, if there is a listener zone property attached with another Speaker zone, We could imagine highlight two zone, with the speakers and attendees
        // Check if there is "listenerMegaphone"
        const speakerZone = areaData.properties.find(
            (property) => property.type === "listenerMegaphone" || property.type === "speakerMegaphone",
        );
        if (speakerZone == undefined) return;

        // Check if there is "speakerZone" or "listenerMegaphone"
        const speakerZoneAreas: AreaData[] = [];
        if (speakerZone.type === "listenerMegaphone") {
            // If "listenerMegaphone", get the speaker zone attached
            const speakerZoneArea = gameManager
                .getCurrentGameScene()
                .getGameMap()
                .getWamFile()
                ?.getGameMapAreas()
                ?.getAreas()
                .get(speakerZone.speakerZoneName);
            if (speakerZoneArea != undefined) speakerZoneAreas.push(speakerZoneArea);
        }
        if (speakerZone.type === "speakerMegaphone") {
            // If "speakerZone", get all listners zone attached
            gameManager
                .getCurrentGameScene()
                .getGameMap()
                .getWamFile()
                ?.getGameMapAreas()
                ?.getAreas()
                .forEach((area) => {
                    if (
                        area.properties.find(
                            (c) => c.type === "listenerMegaphone" && c.speakerZoneName == areaData.id,
                        ) == undefined
                    )
                        return;
                    speakerZoneAreas.push(area);
                });
        }

        if (speakerZoneAreas.length == 0) return;

        // Merge all zone and create new one will be highlighted
        const unionRect = this.mergeZones([...speakerZoneAreas, areaData]);
        if (unionRect == undefined) return;

        this.scene.focusFx.attachToArea(unionRect);
        this.scene.focusFx.setFeather(property.gradientWidth);
        this.scene.focusFx.setColor(Color.HexStringToColor(property.color));
        this.scene.focusFx.setTargetDarkness(property.opacity);
        this.scene.focusFx.setTransitionDuration(property.duration);
        this.scene.focusFx.show();
    }

    private handleJitsiRoomPropertyOnEnter(property: JitsiRoomPropertyData): void {
        const openJitsiRoomFunction = async () => {
            const roomName = Jitsi.slugifyJitsiRoomName(property.roomName, this.scene.roomUrl, property.noPrefix);
            let jitsiUrl = property.jitsiUrl;

            let jwt: string | undefined;
            if (JITSI_PRIVATE_MODE && !jitsiUrl) {
                if (!this.scene.connection) {
                    console.info("Cannot connect to Jitsi. No connection to Pusher server.");
                    return;
                }
                const answer = await this.scene.connection.queryJitsiJwtToken(roomName);
                jwt = answer.jwt;
                jitsiUrl = answer.url;
            }

            jitsiUrl = jitsiUrl || JITSI_URL;
            if (jitsiUrl === undefined) {
                throw new Error("Missing JITSI_URL environment variable or jitsiUrl parameter in the map.");
            }

            if (!jitsiUrl.startsWith("http://") && !jitsiUrl.startsWith("https://")) {
                jitsiUrl = `https://${jitsiUrl}`;
            }

            let parsedUrl: URL;
            try {
                parsedUrl = new URL(jitsiUrl);
            } catch (error) {
                console.error("Invalid Jitsi URL:", jitsiUrl, error);
                throw new Error(`Invalid Jitsi URL: ${jitsiUrl}`, { cause: error });
            }

            inJitsiStore.set(true);
            jitsiMeetingStarted(roomName);

            const coWebsite = new JitsiCoWebsite(
                parsedUrl,
                property.width,
                property.closable,
                roomName,
                gameManager.getPlayerName() ?? "unknown",
                jwt,
                property.jitsiRoomConfig,
                undefined,
                property.jitsiRoomAdminTag ?? null,
            );

            coWebsites.add(coWebsite);

            analyticsClient.trackAdminEvent("meeting.area_entered", {
                roomId: this.scene.roomUrl,
                meetingProvider: "jitsi",
            });

            popupStore.removePopup("jitsi");
            // TODO: this is the code to remove the new design popup before the "new design"
            /*this.scene.CurrentPlayer.destroyText("jitsi");
            const callback = this.actionTriggerCallback.get("jitsi");
            if (callback) {
                this.scene.userInputManager.removeSpaceEventListener(callback);
                this.actionTriggerCallback.delete("jitsi");
            }*/
            /**
             * @DEPRECATED - This is the old way to show trigger message
             layoutManagerActionStore.removeAction("jitsi");
             */
        };

        const forceTrigger = localUserStore.getForceCowebsiteTrigger();
        if (forceTrigger || property.trigger === ON_ACTION_TRIGGER_BUTTON) {
            let message = property.triggerMessage;
            if (message === undefined) {
                message = touchScreenManager.detectPrimaryTouchDevice()
                    ? get(LL).trigger.mobile.jitsiRoom()
                    : get(LL).trigger.jitsiRoom();
            }

            popupStore.addPopup(
                JitsiPopup,
                {
                    message: message,
                    click: () => {
                        openJitsiRoomFunction().catch((e) => console.error(e));
                    },
                    userInputManager: this.scene.userInputManager,
                },
                "jitsi",
            );
            //TODO: bode below is old "new design" popups before the new design. Choose i we keep it.
            // Create callback and play text message
            /*const callback = () => {
                openJitsiRoomFunction().catch((e) => console.error(e));
                this.scene.CurrentPlayer.destroyText("jitsi");
                this.scene.userInputManager.removeSpaceEventListener(callback);
                this.actionTriggerCallback.delete("jitsi");
            };
            this.scene.CurrentPlayer.playText("jitsi", `${message}`, -1, callback);
            this.scene.userInputManager?.addSpaceEventListener(callback);
            this.actionTriggerCallback.set("jitsi", callback);*/

            /**
             * @DEPRECATED - This is the old way to show trigger message
             layoutManagerActionStore.addAction({
             uuid: "jitsi",
             type: "message",
             message: message,
             callback: () => {
             openJitsiRoomFunction().catch((e) => console.error(e));
             },
             userInputManager: this.scene.userInputManager,
             },
             "jitsi"
             );*/
        } else {
            openJitsiRoomFunction().catch((e) => console.error(e));
        }
    }

    private async handleLivekitRoomPropertyOnEnter(
        property: LivekitRoomPropertyData,
        abortSignal: AbortSignal,
    ): Promise<void> {
        inLivekitStore.set(true);

        const roomID = property.roomName.trim().length === 0 ? property.id : property.roomName;

        const roomName = Jitsi.slugifyJitsiRoomName(roomID, this.scene.roomUrl).trim();

        const livekitRoomConfig = property.livekitRoomConfig ?? {
            startWithAudioMuted: false,
            startWithVideoMuted: false,
        };

        if (livekitRoomConfig.startWithAudioMuted && get(requestedMicrophoneState)) {
            this._isMicrophoneActiveBeforeLivekitRoom = true;
            requestedMicrophoneState.disableMicrophone();
            let numberOfCalls = 0;
            this._requestedMicrophoneStateSubscription = requestedMicrophoneState.subscribe(() => {
                numberOfCalls++;
                if (numberOfCalls <= 1) return;
                // we change the values so that if the microphone or camera state changes, we retain those values when leaving the area
                this._isMicrophoneActiveBeforeLivekitRoom = false;
                this._isVideoActiveBeforeLivekitRoom = false;
                this._requestedMicrophoneStateSubscription?.();
            });
        }

        if (livekitRoomConfig.startWithVideoMuted && get(requestedCameraState)) {
            this._isVideoActiveBeforeLivekitRoom = true;
            requestedCameraState.disableWebcam();
            let numberOfCalls = 0;
            this._requestedCameraStateSubscription = requestedCameraState.subscribe(() => {
                numberOfCalls++;
                if (numberOfCalls <= 1) return;
                // we change the values so that if the microphone or camera state changes, we retain those values when leaving the area
                this._isVideoActiveBeforeLivekitRoom = false;
                this._isMicrophoneActiveBeforeLivekitRoom = false;
                this._requestedCameraStateSubscription?.();
            });
        }

        await this.scene.proximityChatRoomManager.joinSpace(
            roomName,
            property.roomName,
            ["cameraState", "microphoneState", "screenSharingState"],
            true,
            FilterType.ALL_USERS,
            property.livekitRoomConfig?.disableChat ?? false,
            abortSignal,
            "meeting",
        );

        analyticsClient.trackAdminEvent("meeting.area_entered", { roomId: this.scene.roomUrl });
    }

    private handleMatrixRoomAreaOnEnter(property: MatrixRoomPropertyData) {
        const isConnected = get(userIsConnected);
        if (this.scene.connection && property.serverData?.matrixRoomId && isConnected) {
            this.scene.connection
                .queryEnterChatRoomArea(property.serverData.matrixRoomId)
                .then(() => {
                    if (!property.serverData?.matrixRoomId) {
                        throw new Error("Failed to join room : roomId is undefined");
                    }
                    return gameManager.chatConnection.joinRoom(property.serverData.matrixRoomId);
                })
                .then((room: ChatRoom | undefined) => {
                    if (!room) return;
                    selectedRoomStore.set(room);
                    navChat.switchToChat();
                    chatZoneLiveStore.set(true);
                    if (property.shouldOpenAutomatically) chatVisibilityStore.set(true);
                })
                .catch((error) => {
                    console.error("Failed to confirm emojis validation", error);
                });
            return;
        }

        if (!isConnected && property.shouldOpenAutomatically) {
            chatVisibilityStore.set(true);
        }
    }

    private handlePersonalAreaPropertyOnEnter(
        property: PersonalAreaPropertyData,
        areaData: AreaData,
        area?: Area,
    ): void {
        if (property.ownerId !== null) {
            canRequestVisitCardsStore.set(true);
            const isMyPersonalArea = property.ownerId === localUserStore.getLocalUser()?.uuid;
            if (isMyPersonalArea) {
                isInsidePersonalAreaStore.set(true);
            } else {
                this.displayPersonalAreaOwnerVisitCard(property.ownerId, areaData, area);
            }
        } else if (property.accessClaimMode === PersonalAreaAccessClaimMode.enum.dynamic) {
            this.displayPersonalAreaClaimDialogBox(property, areaData, area);
        }
    }

    private displayPersonalAreaOwnerVisitCard(ownerId: string, areaData: AreaData, area?: Area) {
        const connection = this.scene.connection;
        if (connection && this.isPersonalAreaOwnerAway(ownerId, areaData)) {
            if (ADMIN_URL) {
                connection
                    .queryMember(ownerId)
                    .then((member: Member) => {
                        if (get(canRequestVisitCardsStore) === false) return;
                        if (member?.visitCardUrl) {
                            requestVisitCardsStore.set(member.visitCardUrl);
                        }
                        if (member?.chatID) {
                            selectedChatIDRemotePlayerStore.set(member?.chatID);
                        }
                    })
                    .catch((error) => console.error(error));
            }

            area?.highLightArea(true);
        }
    }

    private isPersonalAreaOwnerAway(areaOwnerId: string, areaData: AreaData) {
        const playerMap = this.scene.getRemotePlayersRepository().getPlayers();
        let ownerOnMap: MessageUserJoined | undefined = undefined;
        for (const player of playerMap.values()) {
            if (player.userUuid === areaOwnerId) {
                ownerOnMap = player;
            }
        }
        if (ownerOnMap === undefined) {
            return true;
        }
        const { position: userPosition } = ownerOnMap;

        const isOwnerInsidePersonalArea = this.scene.getGameMapFrontWrapper().isInsideAreaByCoordinates(
            {
                x: areaData.x,
                y: areaData.y,
                width: areaData.width,
                height: areaData.height,
            },
            { x: userPosition.x, y: userPosition.y },
        );

        return !isOwnerInsidePersonalArea;
    }

    private displayPersonalAreaClaimDialogBox(property: PersonalAreaPropertyData, areaData: AreaData, area?: Area) {
        const userHasAllowedTagToClaimTheArea =
            localUserStore.isLogged() &&
            (property.allowedTags.length === 0 ||
                property.allowedTags.some((tag) => this.scene.connection?.hasTag(tag)));
        if (userHasAllowedTagToClaimTheArea) {
            area?.highLightArea(true);
            mapEditorAskToClaimPersonalAreaStore.set(areaData);
        }
    }

    private handleSilentPropertyOnEnter(): void {
        silentStore.setAreaSilent(true);
    }

    private handleOpenWebsitePropertiesOnLeave(property: OpenWebsitePropertyData): void {
        const openWebsiteProperty: string | null = property.link;

        if (!openWebsiteProperty) {
            return;
        }

        const coWebsiteOpen = this.openedCoWebsites.get(property.id);

        if (coWebsiteOpen) {
            const coWebsite = coWebsiteOpen.coWebsite;

            if (coWebsite) {
                coWebsites.remove(coWebsite);
            }
        }

        this.openedCoWebsites.delete(property.id);

        inOpenWebsite.set(false);

        if (property.trigger == undefined || property.trigger === ON_ACTION_TRIGGER_ENTER) {
            return;
        }

        const actionStore = get(popupStore);
        const actionTriggerUuid = this.coWebsitesActionTriggers.get(property.id);
        if (!actionTriggerUuid) {
            return;
        }

        const action =
            actionStore && actionStore.length > 0
                ? actionStore.find((action) => action.uuid === actionTriggerUuid)
                : undefined;

        if (action) {
            popupStore.removePopup(actionTriggerUuid);
        }

        this.scene.CurrentPlayer.destroyText(actionTriggerUuid);
        const callback = this.actionTriggerCallback.get(actionTriggerUuid);
        if (callback) {
            this.scene.userInputManager.removeSpaceEventListener(callback);
            this.actionTriggerCallback.delete(actionTriggerUuid);
        }

        /**
         * @DEPRECATED - This is the old way to show trigger message
         const actionStore = get(layoutManagerActionStore);
         const action =
         actionStore && actionStore.length > 0
         ? actionStore.find((action) => action.uuid === actionTriggerUuid)
         : undefined;

         if (action) {
         popupStore.removePopup(actionTriggerUuid);
         }
         */

        this.coWebsitesActionTriggers.delete(property.id);
    }

    private handleFocusablePropertiesOnLeave(property: FocusablePropertyData): void {
        if (!property) {
            return;
        }
        this.scene.getCameraManager().leaveFocusMode(this.scene.CurrentPlayer, 1000);
    }

    private handleHighlightPropertiesOnLeave(property: HighlightPropertyData): void {
        if (!property) {
            return;
        }
        this.scene.focusFx?.hide();
    }

    private handleSilentPropertyOnLeave(): void {
        silentStore.setAreaSilent(false);
    }

    private handlePlayAudioPropertyOnLeave(): void {
        if (get(audioManagerFileStore) != "") audioManagerVolumeStore.stopSound(true);
        if (get(audioManagerFileStore) != "") audioManagerFileStore.unloadAudio();
        audioManagerVisibilityStore.set("hidden");
    }

    private handlePlayAudioPropertyOnUpdate(newProperty: PlayAudioPropertyData): void {
        audioManagerFileStore.unloadAudio();
        audioManagerFileStore.playAudio(newProperty.audioLink, this.scene.getMapUrl(), newProperty.volume);
    }

    private handleJitsiRoomPropertyOnLeave(property: JitsiRoomPropertyData): void {
        popupStore.removePopup("jitsi");
        // TODO: this is the code of the new old popups replaced by the new design. TODO: choose if we keep those.
        /*this.scene.CurrentPlayer.destroyText("jitsi");
        const callback = this.actionTriggerCallback.get("jitsi");
        if (callback) {
            this.scene.userInputManager.removeSpaceEventListener(callback);
            this.actionTriggerCallback.delete("jitsi");
        }*/
        /**
         * @DEPRECATED - This is the old way to show trigger message
         layoutManagerActionStore.removeAction("jitsi");
         */
        coWebsites.keepOnly((coWebsite) => !(coWebsite instanceof JitsiCoWebsite));
        inJitsiStore.set(false);
        jitsiMeetingEnded();
    }

    private handlePersonalAreaPropertyOnLeave(property: PersonalAreaPropertyData, area?: Area): void {
        const isMyPersonalArea = property.ownerId === localUserStore.getLocalUser()?.uuid;
        if (isMyPersonalArea) {
            isInsidePersonalAreaStore.set(false);
        }
        // Reset this store to indicate that the user is no longer in the personal area and cannot request or display their business card.
        canRequestVisitCardsStore.set(false);

        mapEditorAskToClaimPersonalAreaStore.set(undefined);
        if (get(requestVisitCardsStore)) {
            requestVisitCardsStore.set(null);
        }
        area?.unHighLightArea();
    }

    private async handleLivekitRoomPropertyOnLeave(property: LivekitRoomPropertyData): Promise<void> {
        const roomID = property.roomName.trim().length === 0 ? property.id : property.roomName;
        const roomName = Jitsi.slugifyJitsiRoomName(roomID, this.scene.roomUrl, false);

        await this.scene.proximityChatRoomManager.leaveSpace(roomName, true);

        this._requestedMicrophoneStateSubscription?.();
        this._requestedCameraStateSubscription?.();

        if (this._isMicrophoneActiveBeforeLivekitRoom) {
            requestedMicrophoneState.enableMicrophone();
        }
        if (this._isVideoActiveBeforeLivekitRoom) {
            requestedCameraState.enableWebcam();
        }

        this._isVideoActiveBeforeLivekitRoom = false;
        this._isMicrophoneActiveBeforeLivekitRoom = false;
        inLivekitStore.set(false);
    }

    private handleExtensionModuleAreaPropertyOnLeave(subtype: string, area?: AreaData): void {
        const extensionModule = get(extensionModuleStore);
        for (const module of extensionModule) {
            if (!module.areaMapEditor) continue;

            const areaMapEditor = module.areaMapEditor();
            if (
                areaMapEditor == undefined ||
                areaMapEditor[subtype] == undefined ||
                areaMapEditor[subtype].handleAreaPropertyOnLeave == undefined
            )
                continue;

            areaMapEditor[subtype].handleAreaPropertyOnLeave(area);
            inJitsiStore.set(false);
            externalMeetingEnded(subtype);
        }
    }

    private handleExtensionModuleAreaPropertyOnEnter(area: AreaData, subtype: string, signal: AbortSignal): void {
        const extensionModule = get(extensionModuleStore);
        for (const module of extensionModule) {
            if (!module.areaMapEditor) continue;

            const areaMapEditor = module.areaMapEditor();
            if (!areaMapEditor || !areaMapEditor[subtype] || !areaMapEditor[subtype].handleAreaPropertyOnEnter) {
                continue;
            }
            areaMapEditor[subtype].handleAreaPropertyOnEnter(area, signal);
            inJitsiStore.set(true);
            // Only the modules that declare their area a meeting — the same flag the
            // editor and the pathfinder read, so a todo-list panel is not a meeting
            // here either.
            if (areaMapEditor[subtype].isMeeting) {
                externalMeetingStarted(subtype, area.id, this.scene.roomUrl);
            }
        }
    }

    private handleMatrixRoomAreaOnLeave(property: MatrixRoomPropertyData) {
        if (!get(userIsConnected)) {
            chatVisibilityStore.set(false);
            return;
        }

        const actualRoom = get(selectedRoomStore);
        const chatVisibility = get(chatVisibilityStore);

        if (actualRoom?.id === property.serverData?.matrixRoomId && chatVisibility) {
            chatVisibilityStore.set(false);
            selectedRoomStore.set(undefined);
        }
        chatZoneLiveStore.set(false);

        get(gameManager.chatConnection.rooms)
            .find((room) => room.id === property.serverData?.matrixRoomId)
            ?.leaveRoom()
            .catch((error) => console.error(error));

        if (this.scene.connection && property.serverData?.matrixRoomId) {
            this.scene.connection.emitLeaveChatRoomArea(property.serverData.matrixRoomId);
        }
    }

    private openCoWebsiteFunction(
        property: OpenWebsitePropertyData | OpenFilePropertyData,
        coWebsiteOpen: OpenCoWebsite,
        actionId: string,
        analyticsContext: {
            targetUrl?: string;
            triggerProperty?: "openLink" | "openWebsite" | "other";
            areaId?: string;
            areaName?: string;
        } = {},
    ): void {
        // Check URl and get the correct one
        let urlStr = property.link ?? "";
        try {
            urlStr = scriptUtils.getWebsiteUrl(property.link ?? "");
        } catch (e) {
            console.error("Error on getWebsiteUrl: ", e);
        }

        let allowAPI = false;
        if (property.type === "openWebsite") {
            allowAPI = property.allowAPI ?? false;
        }

        // Create the co-website to be opened
        const url = new URL(urlStr, this.scene.mapUrlFile);
        const coWebsite =
            property.type === "openFile" && isImageCoWebsiteUrl(url)
                ? new ImageCoWebsite(
                      url,
                      property.name ?? getImageCoWebsiteTitle(url),
                      property.width,
                      property.closable,
                      property.hideUrl,
                  )
                : new SimpleCoWebsite(
                      url,
                      allowAPI ?? false,
                      property.policy,
                      property.width,
                      property.closable,
                      property.hideUrl,
                  );

        coWebsiteOpen.coWebsite = coWebsite;

        coWebsites.add(coWebsite, undefined, {
            targetUrl: analyticsContext.targetUrl ?? url.toString(),
            triggerProperty:
                analyticsContext.triggerProperty ?? (property.type === "openFile" ? "openLink" : "openWebsite"),
            areaId: analyticsContext.areaId,
            areaName: analyticsContext.areaName,
        });

        this.loadCoWebsiteFunction(coWebsite, actionId);

        //user in a zone with cowebsite opened or pressed SPACE to enter is a zone
        inOpenWebsite.set(true);
    }

    private toCanonicalCowebsiteTargetUrl(link: string): string {
        let normalizedLink = link;
        try {
            normalizedLink = scriptUtils.getWebsiteUrl(link);
        } catch (error) {
            console.error("Error on getWebsiteUrl: ", error);
        }

        return new URL(normalizedLink, this.scene.mapUrlFile).toString();
    }

    private loadCoWebsiteFunction(coWebsite: CoWebsite, actionId: string): void {
        // TODO: this is the code of the old new popups
        /*coWebsiteManager.loadCoWebsite(coWebsite).catch(() => {
            console.error("Error during loading a co-website: " + coWebsite.getUrl());
        });

        this.scene.CurrentPlayer.destroyText(actionId);
        const callback = this.actionTriggerCallback.get(actionId);
        if (callback) {
            this.scene.userInputManager.removeSpaceEventListener(callback);
            this.actionTriggerCallback.delete(actionId);
        }*/
        /**
         * @DEPRECATED - This is the old way to show trigger message
         layoutManagerActionStore.removeAction(actionId);
         */
        // try {
        //     coWebsiteManager.loadCoWebsite(coWebsite)
        // }
        // catch (e) {
        //     console.error("Error during loading a co-website: " + coWebsite.getUrl(), e);
        // };
        popupStore.removePopup(actionId);
    }

    private async handleSpeakerMegaphonePropertyOnEnter(
        property: SpeakerMegaphonePropertyData,
        areaId: string,
        abortSignal: AbortSignal,
    ): Promise<void> {
        if (property.name !== undefined && property.id !== undefined) {
            const uniqRoomName = getAreaProximitySpaceName(property.name, areaId);
            const wasListener = get(isListenerStore);

            // Update stores first so the bubble closes and UI reflects "in a meeting" before stream logic.
            isSpeakerStore.set(true);
            isListenerStore.set(false);

            try {
                // Always go through the manager: it serializes joins and leaves targeting the same
                // space, so a leave enqueued by a zone-exit handler processed in the same frame
                // completes before this join runs. If we are still in the space (overlapping zones,
                // e.g. switching from listener to speaker), the join is a cheap no-op returning the
                // current space, and the room kind is switched to "speaker". Deciding here from
                // getCurrentSpaceName() instead would read state that does not reflect a queued
                // leave yet, and skip the join of a space that is about to be destroyed.
                const joinedRoom = await this.scene.proximityChatRoomManager.joinSpace(
                    uniqRoomName,
                    property.name,
                    getMegaphoneSpaceFields(property.seeAttendees),
                    true,
                    property.seeAttendees
                        ? FilterType.LIVE_STREAMING_USERS_WITH_FEEDBACK
                        : FilterType.LIVE_STREAMING_USERS,
                    !property.chatEnabled,
                    undefined,
                    "speaker",
                );
                const space = joinedRoom.getCurrentSpace();
                if (!space) {
                    throw new Error(`Failed to join megaphone speaker space "${uniqRoomName}"`);
                }

                space.startStreaming();
                currentLiveStreamingSpaceStore.set(space);
                isSpeakerStore.set(true);

                // Track this zone
                this.activeMegaphoneZones.set(property.id, {
                    spaceName: uniqRoomName,
                    role: "speaker",
                    propertyId: property.id,
                    seeAttendees: property.seeAttendees,
                    chatEnabled: property.chatEnabled,
                    allowTalking: false,
                    waitingLink: undefined,
                });
                this.refreshMegaphoneGlobalStores(uniqRoomName);
            } catch (e) {
                isSpeakerStore.set(false);
                isListenerStore.set(wasListener);
                this.refreshMegaphoneGlobalStores(uniqRoomName);
                if (e instanceof AbortError) {
                    return;
                }
                throw e;
            }
        }
    }

    private async handleSpeakerMegaphonePropertyOnLeave(
        property: SpeakerMegaphonePropertyData,
        areaId?: string,
    ): Promise<void> {
        if (property.name !== undefined && property.id !== undefined) {
            const uniqRoomName = getAreaProximitySpaceName(property.name, areaId ?? property.id);

            // Remove from tracking
            this.activeMegaphoneZones.delete(property.id);
            const room = this.scene.proximityChatRoomManager.resolveTargetRoom(uniqRoomName);
            const space = room?.getCurrentSpace();

            if (space) {
                try {
                    space.stopStreaming();
                } catch (error) {
                    console.error("An error occurred while stopping streaming", error);
                    Sentry.captureException(error);
                }
            }

            this.refreshMegaphoneGlobalStores(uniqRoomName);

            // Check if still in a listener zone for the same space
            const remainingListenerZone = this.findActiveListenerZoneForSpace(uniqRoomName);

            if (remainingListenerZone) {
                // Switch back to listener role instead of leaving
                room?.kind.set("listener");
                if (space) {
                    isSpeakerStore.set(false);
                    isListenerStore.set(!this.shouldAllowTalkingInSpace(uniqRoomName));
                    listenerWaitingMediaStore.set(remainingListenerZone.waitingLink);

                    // Restore listener-specific state
                    if (remainingListenerZone.seeAttendees) {
                        space.startListenerStreaming();
                        listenerSharingCameraStore.set(true);
                    } else {
                        listenerSharingCameraStore.set(false);
                    }
                    this.refreshMegaphoneGlobalStores(uniqRoomName);
                    return;
                }
            }

            // Otherwise, do the full leave
            isSpeakerStore.set(false);
            currentLiveStreamingSpaceStore.set(undefined);

            await this.scene.proximityChatRoomManager.leaveSpace(uniqRoomName, true);
        }
    }

    private async handleListenerMegaphonePropertyOnEnter(
        property: ListenerMegaphonePropertyData,
        abortSignal: AbortSignal,
    ): Promise<void> {
        if (property.speakerZoneName !== undefined) {
            const megaphoneAreaInfo = getSpeakerMegaphoneAreaInfo(
                this.scene.getGameMap().getWamFile()?.getGameMapAreas().getAreas(),
                property.speakerZoneName,
            );

            if (!megaphoneAreaInfo) {
                return;
            }

            const { name: speakerZoneName, seeAttendees } = megaphoneAreaInfo;

            {
                const uniqRoomName = getAreaProximitySpaceName(speakerZoneName, property.speakerZoneName);

                // Speaker has priority: if we are also inside a speaker zone of this space, only
                // track this listener zone, don't change the role. Unlike the room state,
                // activeMegaphoneZones is updated synchronously by the enter/leave handlers, so
                // this check cannot be stale.
                const existingSpeakerZone = this.findActiveSpeakerZoneForSpace(uniqRoomName);
                if (existingSpeakerZone) {
                    // Just track this listener zone, but don't change the role
                    this.activeMegaphoneZones.set(property.id, {
                        spaceName: uniqRoomName,
                        role: "listener",
                        propertyId: property.id,
                        seeAttendees,
                        chatEnabled: property.chatEnabled,
                        allowTalking: property.allowTalking,
                        waitingLink: property.waitingLink,
                    });
                    this.refreshMegaphoneGlobalStores(uniqRoomName);
                    return;
                }

                // Always go through the manager (see handleSpeakerMegaphonePropertyOnEnter for why
                // we must not decide from getCurrentSpaceName() here): if we are still in the
                // space, the join is a no-op returning the current space and the room kind is
                // switched to "listener"; if a leave is queued, the join runs after it and rebuilds
                // the space.
                const joinedRoom = await this.scene.proximityChatRoomManager.joinSpace(
                    uniqRoomName,
                    speakerZoneName,
                    getMegaphoneSpaceFields(seeAttendees),
                    true,
                    seeAttendees ? FilterType.LIVE_STREAMING_USERS_WITH_FEEDBACK : FilterType.LIVE_STREAMING_USERS,
                    !property.chatEnabled,
                    undefined,
                    "listener",
                );
                const space = joinedRoom.getCurrentSpace();
                if (!space) {
                    throw new Error(`Failed to join megaphone listener space "${uniqRoomName}"`);
                }
                currentLiveStreamingSpaceStore.set(space);
                listenerWaitingMediaStore.set(property.waitingLink);

                listenerSharingCameraStore.set(seeAttendees);
                // Use startListenerStreaming() instead of startStreaming()
                // This enables streaming WITHOUT setting megaphoneState=true,
                // so the listener remains invisible to other listeners
                if (seeAttendees) {
                    space.startListenerStreaming();
                }

                // Track this zone
                this.activeMegaphoneZones.set(property.id, {
                    spaceName: uniqRoomName,
                    role: "listener",
                    propertyId: property.id,
                    seeAttendees,
                    chatEnabled: property.chatEnabled,
                    allowTalking: property.allowTalking,
                    waitingLink: property.waitingLink,
                });
                isListenerStore.set(!property.allowTalking);
                this.refreshMegaphoneGlobalStores(uniqRoomName);
            }
        }
    }

    private async handleListenerMegaphonePropertyOnLeave(property: ListenerMegaphonePropertyData): Promise<void> {
        if (property.speakerZoneName !== undefined) {
            const speakerZoneName = getSpeakerMegaphoneAreaName(
                this.scene.getGameMap().getWamFile()?.getGameMapAreas().getAreas(),
                property.speakerZoneName,
            );
            if (speakerZoneName !== undefined) {
                const uniqRoomName = getAreaProximitySpaceName(speakerZoneName, property.speakerZoneName);

                // Remove from tracking
                this.activeMegaphoneZones.delete(property.id);
                this.refreshMegaphoneGlobalStores(uniqRoomName);

                // Check if still in a speaker zone for the same space
                const remainingSpeakerZone = this.findActiveSpeakerZoneForSpace(uniqRoomName);
                if (remainingSpeakerZone) {
                    // Still in speaker zone, don't leave the space
                    return;
                }

                // Check if still in another listener zone for the same space
                const remainingListenerZone = this.findActiveListenerZoneForSpace(uniqRoomName);
                if (remainingListenerZone) {
                    // Still in another listener zone, update mute state based on remaining zones
                    isListenerStore.set(!this.shouldAllowTalkingInSpace(uniqRoomName));
                    this.refreshMegaphoneGlobalStores(uniqRoomName);
                    return;
                }

                await this.scene.proximityChatRoomManager.leaveSpace(uniqRoomName, true);

                currentLiveStreamingSpaceStore.set(undefined);
                isListenerStore.set(false);
                listenerWaitingMediaStore.set(undefined);
                // Reset seeAttendees camera sharing state
                listenerSharingCameraStore.set(false);
                this.refreshMegaphoneGlobalStores(uniqRoomName);
            }
        }
    }

    private refreshMegaphoneGlobalStores(preferredSpaceName?: string): void {
        const zones = Array.from(this.activeMegaphoneZones.values());
        const speakerZone = zones.find((zone) => zone.role === "speaker");
        const listenerZone =
            (preferredSpaceName
                ? zones.find((zone) => zone.spaceName === preferredSpaceName && zone.role === "listener")
                : undefined) ?? zones.find((zone) => zone.role === "listener");

        isSpeakerStore.set(speakerZone !== undefined);
        isListenerStore.set(
            speakerZone === undefined && zones.some((zone) => zone.role === "listener" && !zone.allowTalking),
        );

        const activeZone = speakerZone ?? listenerZone;
        if (!activeZone) {
            currentLiveStreamingSpaceStore.set(undefined);
            listenerWaitingMediaStore.set(undefined);
            listenerSharingCameraStore.set(false);
            return;
        }

        currentLiveStreamingSpaceStore.set(
            this.scene.proximityChatRoomManager.resolveTargetRoom(activeZone.spaceName)?.getCurrentSpace(),
        );
        listenerWaitingMediaStore.set(listenerZone?.waitingLink);
        listenerSharingCameraStore.set(listenerZone?.seeAttendees ?? false);
    }

    /**
     * Finds an active listener zone for a given space name.
     * Used to determine if we should switch roles instead of leaving the space.
     */
    private findActiveListenerZoneForSpace(spaceName: string): MegaphoneZoneState | undefined {
        for (const zone of this.activeMegaphoneZones.values()) {
            if (zone.spaceName === spaceName && zone.role === "listener") {
                return zone;
            }
        }
        return undefined;
    }

    /**
     * Checks if talking should be allowed in a space by examining all active listener zones.
     * Returns true only if all active listener zones for the space have allowTalking=true.
     * If any zone has allowTalking=false, the user should be muted.
     */
    private shouldAllowTalkingInSpace(spaceName: string): boolean {
        for (const zone of this.activeMegaphoneZones.values()) {
            if (zone.spaceName === spaceName && zone.role === "listener" && !zone.allowTalking) {
                return false;
            }
        }
        return true;
    }

    /**
     * Finds an active speaker zone for a given space name.
     * Used to determine if we should switch roles instead of leaving the space.
     */
    private findActiveSpeakerZoneForSpace(spaceName: string): MegaphoneZoneState | undefined {
        for (const zone of this.activeMegaphoneZones.values()) {
            if (zone.spaceName === spaceName && zone.role === "speaker") {
                return zone;
            }
        }
        return undefined;
    }

    private handleExitPropertyOnEnter(url: string): void {
        this.scene
            .onMapExit(Room.getRoomPathFromExitUrl(url, window.location.toString()))
            .catch((e) => console.error(e));
    }

    private handleTooltipPropertyOnEnter(property: TooltipPropertyData): void {
        // Calculate the duration. If the value is 0 or -1, the duration is infinite and we set it to -1
        // If the duration is more than 0, we convert second (value used in the map editor) to milliseconds
        const duration = property.duration < 1 ? -1 : property.duration * 1000;

        // Implement the logic to show the info bulle
        this.scene.CurrentPlayer.playText(property.id, property.content, duration);
    }

    private handleTooltipPropertyOnLeave(property: TooltipPropertyData): void {
        // Implement the logic to hide the info bulle
        this.scene.CurrentPlayer.destroyText(property.id);
    }

    private async handleOpenFileOnEnter(
        initialProperty: OpenFilePropertyData,
        areaData: AreaData,
        abortSignal: AbortSignal,
    ): Promise<void> {
        if (!initialProperty.link) {
            return;
        }

        if (!this.scene.connection) {
            console.info("Cannot open file. No connection to Pusher server.");
            return;
        }

        const property = {
            ...initialProperty,
        };

        const answer = await this.scene.connection?.queryMapStorageJwtToken(abortSignal);

        const url = new URL(initialProperty.link);
        url.searchParams.set("token", answer.jwt);

        property.link = url.toString();

        const actionId = "openWebsite-" + uuidv4();

        if (property.newTab) {
            const forceTrigger = localUserStore.getForceCowebsiteTrigger();
            if (forceTrigger || property.trigger === ON_ACTION_TRIGGER_BUTTON) {
                this.coWebsitesActionTriggers.set(property.id, actionId);
                let message = property.triggerMessage;
                if (message === undefined) {
                    message = touchScreenManager.detectPrimaryTouchDevice()
                        ? get(LL).trigger.mobile.newTab()
                        : get(LL).trigger.newTab();
                }

                popupStore.addPopup(
                    PopUpTab,
                    {
                        message: message,
                        click: () => {
                            popupStore.removePopup(actionId);
                            scriptUtils.openTab(url.toString());
                        },
                        userInputManager: this.scene.userInputManager,
                    },
                    actionId,
                );
            } else {
                scriptUtils.openTab(url.toString());
            }
            return;
        }

        if (this.openedCoWebsites.has(property.id)) {
            return;
        }

        const coWebsiteOpen: OpenCoWebsite = {
            actionId: actionId,
        };

        this.openedCoWebsites.set(property.id, coWebsiteOpen);

        if (localUserStore.getForceCowebsiteTrigger() || property.trigger === ON_ACTION_TRIGGER_BUTTON) {
            let message = property.triggerMessage;
            if (!message) {
                message = touchScreenManager.detectPrimaryTouchDevice()
                    ? get(LL).trigger.mobile.cowebsite()
                    : get(LL).trigger.cowebsite();
            }

            this.coWebsitesActionTriggers.set(property.id, actionId);

            popupStore.addPopup(
                FilePopup,
                {
                    message: message,
                    click: () => {
                        this.openCoWebsiteFunction(property, coWebsiteOpen, actionId, {
                            targetUrl: this.toCanonicalCowebsiteTargetUrl(initialProperty.link ?? ""),
                            triggerProperty: "openLink",
                            areaId: areaData.id,
                            areaName: areaData.name,
                        });
                    },
                    userInputManager: this.scene.userInputManager,
                },
                actionId,
            );
        } else if (property.trigger === ON_ICON_TRIGGER_BUTTON) {
            let cowebsiteUrl = url.toString() ?? "";
            try {
                cowebsiteUrl = scriptUtils.getWebsiteUrl(url.toString() ?? "");
            } catch (e) {
                console.error("Error on getWebsiteUrl: ", e);
            }
            const imageUrl = new URL(cowebsiteUrl, this.scene.mapUrlFile);
            const coWebsite = isImageCoWebsiteUrl(imageUrl)
                ? new ImageCoWebsite(
                      imageUrl,
                      property.name ?? getImageCoWebsiteTitle(imageUrl),
                      property.width,
                      property.closable,
                      property.hideUrl,
                  )
                : new SimpleCoWebsite(
                      imageUrl,
                      false,
                      property.policy,
                      property.width,
                      property.closable,
                      property.hideUrl,
                  );

            coWebsiteOpen.coWebsite = coWebsite;

            coWebsites.add(coWebsite, undefined, {
                targetUrl: this.toCanonicalCowebsiteTargetUrl(initialProperty.link ?? ""),
                triggerProperty: "openLink",
                areaId: areaData.id,
                areaName: areaData.name,
            });

            //user in zone to open cowesite with only icon
            inOpenWebsite.set(true);
        }
        if (property.trigger == undefined || property.trigger === ON_ACTION_TRIGGER_ENTER) {
            this.openCoWebsiteFunction(property, coWebsiteOpen, actionId, {
                targetUrl: this.toCanonicalCowebsiteTargetUrl(initialProperty.link ?? ""),
                triggerProperty: "openLink",
                areaId: areaData.id,
                areaName: areaData.name,
            });
        }
    }

    private handleOpenFileOnLeave(property: OpenFilePropertyData): void {
        const openWebsiteProperty: string | null = property.link;

        if (!openWebsiteProperty) {
            return;
        }

        const coWebsiteOpen = this.openedCoWebsites.get(property.id);

        if (coWebsiteOpen) {
            const coWebsite = coWebsiteOpen.coWebsite;

            if (coWebsite) {
                coWebsites.remove(coWebsite);
            }
        }

        this.openedCoWebsites.delete(property.id);

        inOpenWebsite.set(false);

        if (property.trigger == undefined || property.trigger === ON_ACTION_TRIGGER_ENTER) {
            return;
        }

        const actionStore = get(popupStore);
        const actionTriggerUuid = this.coWebsitesActionTriggers.get(property.id);
        if (!actionTriggerUuid) {
            return;
        }

        const action =
            actionStore && actionStore.length > 0
                ? actionStore.find((action) => action.uuid === actionTriggerUuid)
                : undefined;

        if (action) {
            popupStore.removePopup(actionTriggerUuid);
        }

        this.scene.CurrentPlayer.destroyText(actionTriggerUuid);
        const callback = this.actionTriggerCallback.get(actionTriggerUuid);
        if (callback) {
            this.scene.userInputManager.removeSpaceEventListener(callback);
            this.actionTriggerCallback.delete(actionTriggerUuid);
        }

        /**
         * @DEPRECATED - This is the old way to show trigger message
         const actionStore = get(layoutManagerActionStore);
         const action =
         actionStore && actionStore.length > 0
         ? actionStore.find((action) => action.uuid === actionTriggerUuid)
         : undefined;

         if (action) {
         popupStore.removePopup(actionTriggerUuid);
         }
         */

        this.coWebsitesActionTriggers.delete(property.id);
    }

    /**
     * Cleans up all subscriptions and resources.
     * Must be called when the AreasPropertiesListener is no longer needed to prevent memory leaks.
     */
    public destroy(): void {
        // Unsubscribe from variable changes (inner subscription)
        if (this._variableChangesSubscription) {
            this._variableChangesSubscription();
            this._variableChangesSubscription = undefined;
        }

        // Unsubscribe from area property variables manager store (outer subscription)
        if (this._areaPropertyVariablesSubscription) {
            this._areaPropertyVariablesSubscription();
            this._areaPropertyVariablesSubscription = undefined;
        }

        // Clean up microphone/camera state subscriptions
        if (this._requestedMicrophoneStateSubscription) {
            this._requestedMicrophoneStateSubscription();
            this._requestedMicrophoneStateSubscription = undefined;
        }

        if (this._requestedCameraStateSubscription) {
            this._requestedCameraStateSubscription();
            this._requestedCameraStateSubscription = undefined;
        }

        // Abort all pending operations
        for (const abortController of this.abortControllers.values()) {
            abortController.abort();
        }
        this.abortControllers.clear();

        // Clean up action trigger callbacks
        for (const callback of this.actionTriggerCallback.values()) {
            this.scene.userInputManager.removeSpaceEventListener(callback);
        }
        this.actionTriggerCallback.clear();

        // Clean up co-websites
        for (const coWebsiteOpen of this.openedCoWebsites.values()) {
            if (coWebsiteOpen.coWebsite) {
                coWebsites.remove(coWebsiteOpen.coWebsite);
            }
        }
        this.openedCoWebsites.clear();
        this.coWebsitesActionTriggers.clear();

        // Reset lock-related stores
        currentPlayerLockableAreasStore.set([]);
    }
}
